import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { publicResumeService } from '../services/api';
import { ArrowLeft, Download, ExternalLink } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const PublicResumeView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const resumeName = html?.match(/<title[^>]*>(.*?)<\/title>/i)?.[1]?.trim();
  usePageMeta({
    title: resumeName ? `${resumeName} — Resume | 3A Tech` : 'Resume | 3A Tech',
    description: 'View a resume shared on 3A Tech. Download it as a PDF for free.',
  });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    publicResumeService
      .fetchHtml(slug)
      .then((content) => {
        if (!cancelled) setHtml(content);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleDownload = async () => {
    if (!slug) return;
    try {
      const response = await fetch(publicResumeService.pdfUrl(slug));
      if (!response.ok) return;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore download failures on the public page
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E4E4E7] px-4 sm:px-8 h-14 flex items-center justify-between gap-4">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </button>

        <div className="flex items-center gap-1.5 text-sm text-zinc-500">
          Powered by <span className="font-display font-semibold text-brand-600">3A Tech</span>
        </div>

        <div className="flex items-center gap-2">
          {html && (
            <button
              onClick={handleDownload}
              className="h-9 px-4 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-8">
        {!slug || error ? (
          <div className="max-w-md mx-auto mt-16 text-center">
            <h1 className="font-display text-xl font-semibold text-foreground mb-2">Resume not found</h1>
            <p className="text-sm text-zinc-500 mb-6">
              This link is invalid or the resume is no longer public.
            </p>
            <button
              onClick={() => navigate('/')}
              className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Go to 3A Tech
            </button>
          </div>
        ) : !html ? (
          <div className="flex items-center justify-center h-64 text-sm text-zinc-400">Loading resume...</div>
        ) : (
          <iframe
            title="Resume"
            srcDoc={html}
            sandbox="allow-same-origin"
            className="w-full h-[calc(100vh-8rem)] rounded-card border border-zinc-200 bg-white"
          />
        )}
      </main>
    </div>
  );
};

export default PublicResumeView;
